import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Leaf, Droplet, Activity, Apple, Moon, Syringe, ArrowRight, Loader2, CheckCircle2, Circle, Trophy, RotateCcw, Share2, Download, ShieldCheck, Flame } from "lucide-react";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import { getLearnIndex, registerCertificate } from "../lib/api";
import { getRead, toggleRead, clearRead, getCertId, recordVisit, getStreak } from "../lib/wellnessProgress";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

const ICONS = {
  "how-much-water-should-i-drink": Droplet,
  "how-much-exercise-do-i-need": Activity,
  "healthy-eating-basics": Apple,
  "how-much-sleep-do-i-need": Moon,
  "which-vaccines-do-adults-need": Syringe,
};

export default function WellnessPage() {
  const [topics, setTopics] = useState(null);
  const tr = useT();
  const [read, setRead] = useState(getRead());
  const [certName, setCertName] = useState("");
  const [certId] = useState(getCertId);
  const [streak] = useState(() => {
    recordVisit();
    return getStreak();
  });
  const issuedOn = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const certRef = useRef(null);

  useEffect(() => {
    getLearnIndex()
      .then((pages) => setTopics(pages.filter((p) => p.section === "Wellness")))
      .catch(() => setTopics([]));
  }, []);

  const toggle = (slug) => {
    const next = toggleRead(slug);
    setRead(next);
    if (topics && next.includes(slug) && topics.every((t) => next.includes(t.slug))) {
      toast.success("🎉 You've explored every wellness topic. Great job!");
    }
  };
  const readCount = topics ? topics.filter((t) => read.includes(t.slug)).length : 0;
  const allDone = topics && topics.length > 0 && readCount === topics.length;
  const verifyUrl = `${window.location.origin}/verify/${certId}`;

  useEffect(() => {
    if (allDone) registerCertificate(certId, certName.trim());
  }, [allDone, certId, certName]);
  const resetProgress = () => {
    clearRead();
    setRead([]);
    toast.success("Progress reset — explore your topics fresh!");
  };

  const downloadCertificate = async () => {
    if (!certRef.current) return;
    try {
      const canvas = await html2canvas(certRef.current, { backgroundColor: "#ffffff", scale: 2 });
      const link = document.createElementr("a");
      link.download = `healthy-living-explorer-${certId}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Certificate saved as an image!");
    } catch {
      toast.error("Couldn't create the image. Please try again.");
    }
  };

  const shareCertificate = async () => {
    const who = certName.trim() ? `${certName.trim()} is` : "I'm";
    const text = `${who} a certified Healthy-Living Explorer on AskMyDoctor AI — I explored all 5 everyday wellness topics! 🌿`;
    const url = `${window.location.origin}/wellness`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Healthy-Living Explorer", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Certificate message copied — paste it anywhere to share!");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="min-h-screen" data-testid="wellness-page">
      <section className="bg-gradient-to-b from-emerald-50 to-transparent border-b border-emerald-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 font-semibold text-sm px-4 py-1.5 rounded-full mb-5">
            <Leaf className="w-4 h-4" /> {tr("everyday_healthy")}
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tight">
            {tr("well_title")}
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
{tr("well_subtitle")}
          </p>
          {topics && topics.length > 0 && (
            <div className="mt-6" data-testid="wellness-progress">
              <div className="flex items-center justify-between text-sm font-semibold text-slate-600 mb-1.5">
                <span>{readCount} / {topics.length} {tr("well_progress")}</span>
                <span>{Math.round((readCount / topics.length) * 100)}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-emerald-100 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${(readCount / topics.length) * 100}%` }}
                />
              </div>
              {allDone && (
                <div className="mt-4 inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-bold text-base px-4 py-2 rounded-full" data-testid="wellness-all-done">
                  <Trophy className="w-5 h-5" /> {tr("well_all_done")}
                </div>
              )}
              {streak.current >= 2 && (
                <span
                  data-testid="wellness-streak"
                  className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 font-bold text-sm px-3 py-1.5 rounded-full"
                >
                  <Flame className="w-4 h-4" /> {streak.current}-day streak
                </span>
              )}
              {readCount > 0 && (
                <button
                  onClick={resetProgress}
                  data-testid="wellness-reset"
                  className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"
                >
                  <RotateCcw className="w-4 h-4" /> {tr("well_reset")}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {allDone && (
          <>
          <div ref={certRef} className="mb-10 rounded-3xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-8 text-center shadow-sm" data-testid="wellness-certificate">
            <div className="mx-auto mb-5 flex items-center justify-center gap-3" data-testid="certificate-logo">
              <svg width="52" height="52" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="3" y="3" width="58" height="58" rx="17" fill="#0d9488" />
                <path d="M14 34h8l4-11 6 22 4-13 3 6h11" stroke="#ffffff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
              <div className="text-left leading-none">
                <p className="font-heading font-extrabold text-xl text-slate-900">AskMyDoctor <span className="text-teal-600">AI</span></p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Virtual Medical Assistant</p>
              </div>
            </div>
            <p className="text-sm font-bold uppercase tracking-widest text-amber-700">{tr("cert_title")}</p>
            <h2 className="mt-2 font-heading font-extrabold text-3xl text-slate-900">Healthy-Living Explorer</h2>
            <p className="mt-2 text-lg text-slate-600">
              {certName.trim() ? `${tr("cert_awarded_you")}: ${certName.trim()}` : tr("cert_awarded_you")}
            </p>
            {streak.current >= 2 && (
              <div className="mt-3 inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 font-bold text-sm px-3 py-1 rounded-full" data-testid="certificate-streak">
                <Flame className="w-4 h-4" /> {streak.current}-day return streak
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 border-t border-amber-200/70 pt-4 text-xs font-semibold text-slate-500">
              <span className="font-mono tracking-wider text-slate-600" data-testid="certificate-id">ID · {certId}</span>
              <span data-testid="certificate-date">Issued {issuedOn}</span>
            </div>
            <div className="mt-4 flex flex-col items-center gap-1.5" data-testid="certificate-qr">
              <div className="bg-white p-2 rounded-lg border border-amber-200">
                <QRCodeCanvas value={verifyUrl} size={76} bgColor="#ffffff" fgColor="#0f766e" level="M" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Scan to verify</p>
            </div>
            <p className="mt-3 text-[11px] text-slate-400" data-testid="certificate-verify-url">
              Verify at {verifyUrl.replace(/^https?:\/\//, "")}
            </p>
          </div>
          <div className="mb-10 -mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <input
              value={certName}
              onChange={(e) => setCertName(e.target.value)}
              placeholder={tr("cert_name_ph")}
              data-testid="certificate-name-input"
              className="w-full sm:w-64 bg-white border-2 border-amber-200 focus:border-amber-400 rounded-xl px-4 py-2.5 text-base text-slate-800 outline-none text-center"
            />
            <button
              onClick={shareCertificate}
              data-testid="certificate-share"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-base px-6 py-2.5 rounded-xl transition-transform active:scale-95"
            >
              <Share2 className="w-5 h-5" /> Share
            </button>
            <button
              onClick={downloadCertificate}
              data-testid="certificate-download"
              className="inline-flex items-center gap-2 border-2 border-amber-300 text-amber-700 hover:bg-amber-100 font-bold text-base px-6 py-2.5 rounded-xl transition-colors"
            >
              <Download className="w-5 h-5" /> {tr("cert_download")}
            </button>
          </div>
          <div className="mb-10 -mt-4 text-center">
            <Link
              to={`/verify/${certId}`}
              data-testid="certificate-verify-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
            >
              <ShieldCheck className="w-4 h-4" /> {tr("cert_verify")}
            </Link>
          </div>
          </>
        )}
        {topics === null ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> {tr("well_loading")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {topics.map((t) => {
              const Icon = ICONS[t.slug] || Leaf;
              const done = read.includes(t.slug);
              return (
                <div
                  key={t.slug}
                  data-testid={`wellness-topic-${t.slug}`}
                  className={`group rounded-2xl border-2 p-6 transition-all ${
                    done ? "border-emerald-300 bg-emerald-50/50" : "border-emerald-100 bg-white hover:border-emerald-400 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 shrink-0 group-hover:bg-emerald-200 transition-colors">
                      <Icon className="w-6 h-6" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <Link to={`/${t.slug}`} className="block">
                        <h2 className="font-heading font-bold text-xl text-slate-900 leading-snug hover:text-emerald-700">{t.title}</h2>
                        {t.blurb && <p className="mt-1.5 text-base text-slate-600 leading-relaxed">{t.blurb}</p>}
                      </Link>
                      <div className="mt-3 flex items-center gap-4">
                        <Link to={`/${t.slug}`} className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold text-base">
                          {tr("read_more")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <button
                          onClick={() => toggle(t.slug)}
                          data-testid={`wellness-mark-read-${t.slug}`}
                          className={`inline-flex items-center gap-1.5 text-base font-semibold transition-colors ${
                            done ? "text-emerald-700" : "text-slate-400 hover:text-emerald-600"
                          }`}
                        >
                          {done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                          {done ? tr("marked_read") : tr("mark_read")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
